/* global BigInt */
// Pure helpers (no React) for vesting lock-wallet delegation lookup, calldata and simulation.
// Source of truth = on-chain RPC reads; subgraph data is used for discovery + names.

export const NETWORKS = {
  ethereum: {
    label: 'Ethereum mainnet (L1)', short: 'Ethereum',
    rpc: 'https://ethereum-rpc.publicnode.com',
    staking: '0xF55041E37E12cD407ad00CE2910B8269B01263b9',
    epochManager: '0x64F990Bf16552A693dCB043BB7bf3866c5E05DdB',
    explorer: 'https://etherscan.io', explorerName: 'Etherscan',
    epochHours: 24, // 7,200 blocks × 12s
  },
  arbitrum: {
    label: 'Arbitrum One (L2)', short: 'Arbitrum',
    rpc: 'https://arb1.arbitrum.io/rpc',
    staking: '0x00669A4CF01450B64E8A2A20E9b1FCB71E61eF03',
    epochManager: '0x5A843145c43d328B9bB7a4401d94918f131bB281',
    explorer: 'https://arbiscan.io', explorerName: 'Arbiscan',
    epochHours: 24, // 7,200 L1 blocks (Arbitrum epochs track L1 block numbers)
    // Graph Horizon is live on Arbitrum: delegations are keyed by (indexer, verifier).
    // Legacy delegations use the SubgraphService as verifier. Lock wallets still only
    // whitelist the legacy undelegate(address,uint256)/withdrawDelegated(address,address).
    horizon: true,
    subgraphService: '0xb2bb92d0de618878e438b55d5846cfecd9301105',
  },
};

export const SEL = {
  getDelegation: '0x15049a5a',
  getDelegationH: '0xccebcabb', // Horizon getDelegation(sp, verifier, delegator)
  delegationPools: '0x92511c8f',
  currentEpoch: '0x76671808',
  surplusAmount: '0x0dff24d5',
  releasableAmount: '0x5b940081',
  beneficiary: '0x38af3eed',
  undelegate: '0x4d99dd16',
  withdrawDelegated: '0x51a60b02',
  release: '0x86d1a69f',
  withdrawSurplus: '0xb0d1818c',
};

export const isAddr = (a) => /^0x[0-9a-fA-F]{40}$/.test(a || '');
export const pad = (hex) => String(hex).replace(/^0x/, '').toLowerCase().padStart(64, '0');
export const word = (res, i) => BigInt('0x' + ((res || '0x').slice(2 + i * 64, 2 + (i + 1) * 64) || '0'));
export const fmt = (wei) => (Number(BigInt(wei) / 10n ** 12n) / 1e6).toLocaleString('en-US', { maximumFractionDigits: 2 });

export const calldata = {
  undelegate: (indexer, shares) => SEL.undelegate + pad(indexer) + pad(BigInt(shares).toString(16)),
  withdrawDelegated: (indexer) => SEL.withdrawDelegated + pad(indexer) + pad('0'),
  release: () => SEL.release,
  withdrawSurplus: (amount) => SEL.withdrawSurplus + pad(BigInt(amount).toString(16)),
};

async function rpc(net, method, params, fetchImpl = fetch) {
  const r = await fetchImpl(NETWORKS[net].rpc, {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
  });
  return r.json();
}
export async function ethCall(net, to, data, from) {
  const j = await rpc(net, 'eth_call', [{ to, data, ...(from ? { from } : {}) }, 'latest']);
  if (j.error) throw Object.assign(new Error(j.error.message), { data: j.error.data });
  return j.result;
}

// Decode Error(string) revert data.
const CUSTOM = {
  '0x3f199628': 'Nothing thawing (nothing undelegated yet, or still thawing)',
  '0x19e9a8e0': 'Nothing to withdraw yet (still thawing or nothing undelegated)',
  '0xab997935': 'Not enough delegated shares',
  '0x7318ad99': 'Shares cannot be zero',
};
export function revertReason(err) {
  const d = (err && (err.data?.data || err.data)) || '';
  if (typeof d === 'string' && CUSTOM[d.slice(0, 10)]) return CUSTOM[d.slice(0, 10)];
  if (typeof d === 'string' && d.startsWith('0x08c379a0')) {
    const len = Number(BigInt('0x' + d.slice(74, 138)));
    const hex = d.slice(138, 138 + len * 2);
    let s = ''; for (let i = 0; i < hex.length; i += 2) s += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
    return s;
  }
  const m = /reverted:?\s*(.*)$/i.exec(err?.message || '');
  return (m && m[1]) || err?.message || 'reverted';
}

// ---------- subgraph ----------
async function gql(url, query, variables) {
  const r = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ query, variables }) });
  const j = await r.json();
  if (j.errors) throw new Error(j.errors[0].message);
  return j.data;
}

export async function resolveLockWallets(vestingUrl, address) {
  const a = address.toLowerCase();
  const d = await gql(vestingUrl, `query($a: ID!, $b: String!) {
    w: tokenLockWallet(id: $a) { id beneficiary }
    ws: tokenLockWallets(where: { beneficiary: $b }) { id beneficiary }
  }`, { a, b: a });
  if (d.w) return { role: 'lock wallet', wallets: [d.w] };
  return { role: d.ws.length ? 'beneficiary' : 'unknown', wallets: d.ws };
}

export async function subgraphDelegations(networkUrl, delegators) {
  const d = await gql(networkUrl, `query($d: [String!]) {
    _meta { block { number } }
    graphNetworks(first: 1) { currentEpoch delegationUnbondingPeriod }
    delegatedStakes(first: 100, where: { delegator_in: $d }) {
      id shareAmount lockedTokens lockedUntil transferredToL2
      delegator { id }
      indexer { id defaultDisplayName url }
    }
  }`, { d: delegators.map((x) => x.toLowerCase()) });
  return d;
}

// ---------- on-chain ----------
export async function readLockWallet(net, lock) {
  const [sur, rel, ben] = await Promise.all([
    ethCall(net, lock, SEL.surplusAmount).catch(() => null),
    ethCall(net, lock, SEL.releasableAmount).catch(() => null),
    ethCall(net, lock, SEL.beneficiary).catch(() => null),
  ]);
  return {
    surplus: sur ? word(sur, 0) : 0n,
    releasable: rel ? word(rel, 0) : 0n,
    beneficiary: ben ? '0x' + ben.slice(-40) : null,
  };
}

export async function readDelegation(net, indexer, delegator) {
  const n = NETWORKS[net];
  if (n.horizon) {
    // Horizon: on-chain gives shares; thaw state comes from the subgraph + simulation.
    const [d, ep] = await Promise.all([
      ethCall(net, n.staking, SEL.getDelegationH + pad(indexer) + pad(n.subgraphService) + pad(delegator)),
      ethCall(net, n.epochManager, SEL.currentEpoch),
    ]);
    return { indexer, shares: word(d, 0), locked: null, until: null, epoch: word(ep, 0), value: null };
  }
  const [d, pool, ep] = await Promise.all([
    ethCall(net, n.staking, SEL.getDelegation + pad(indexer) + pad(delegator)),
    ethCall(net, n.staking, SEL.delegationPools + pad(indexer)),
    ethCall(net, n.epochManager, SEL.currentEpoch),
  ]);
  const shares = word(d, 0), locked = word(d, 1), until = word(d, 2);
  // DelegationPool getter: cooldownBlocks, indexingRewardCut, queryFeeCut, updatedAtBlock, tokens, shares
  const poolTokens = word(pool, 4), poolShares = word(pool, 5);
  return {
    indexer, shares, locked, until, epoch: word(ep, 0),
    value: poolShares > 0n ? (shares * poolTokens) / poolShares : 0n,
  };
}

// Dry-run one tx with eth_call + eth_estimateGas from the beneficiary.
export async function simulate(net, from, to, data) {
  try {
    const j = await rpc(net, 'eth_call', [{ from, to, data, gas: '0x1c9c380' }, 'latest']);
    if (j.error) throw Object.assign(new Error(j.error.message), { data: j.error.data });
    const g = await rpc(net, 'eth_estimateGas', [{ from, to, data }]);
    return { ok: true, gas: g.result ? BigInt(g.result) : null };
  } catch (e) {
    return { ok: false, reason: revertReason(e) };
  }
}

// Build the step list + status for one delegation.
export function buildSteps(net, deleg, lockInfo) {
  const n = NETWORKS[net];
  if (deleg.locked === null) { // Horizon: rely on subgraph thaw info if present, else simulation decides
    deleg = { ...deleg, locked: BigInt(deleg.sgLocked || 0), until: BigInt(deleg.sgUntil || 0), value: deleg.value ?? 0n };
  }
  const left = deleg.locked > 0n && deleg.until > deleg.epoch ? deleg.until - deleg.epoch : 0n;
  const daysLeft = Math.ceil(Number(left) * n.epochHours / 24);
  return [
    { key: 'undelegate', title: 'Undelegate (start the 28-epoch wait)', data: calldata.undelegate(deleg.indexer, deleg.shares),
      ready: deleg.shares > 0n,
      notReady: deleg.locked > 0n ? 'Already undelegated — waiting to unlock.' : 'No delegated shares left.',
      does: `Unstakes all ${fmt(deleg.shares)} shares${deleg.value ? ` (≈ ${fmt(deleg.value)} GRT)` : ''}. Starts ~28 days of thawing; rewards stop.` },
    { key: 'withdrawDelegated', title: 'withdrawDelegated (after the wait)', data: calldata.withdrawDelegated(deleg.indexer),
      ready: deleg.locked > 0n && left === 0n,
      notReady: deleg.locked > 0n ? `Unlocks in ~${daysLeft} day(s) (epoch ${deleg.until}, now ${deleg.epoch}).` : 'Do step 1 first.',
      does: `Moves the thawed ${fmt(deleg.locked)} GRT from Staking back into the lock wallet.` },
    { key: 'release', title: 'release() — send vested GRT to you', data: calldata.release(),
      ready: lockInfo.releasable > 0n, notReady: 'Nothing releasable yet (do steps 1–2 first, or tokens are still vesting).',
      does: `Sends ${fmt(lockInfo.releasable)} GRT of vested tokens to your beneficiary wallet.` },
    { key: 'withdrawSurplus', title: 'withdrawSurplus(amount) — send rewards above the vesting total', data: calldata.withdrawSurplus(lockInfo.surplus),
      ready: lockInfo.surplus > 0n, notReady: 'No surplus yet (rewards come back with step 2).',
      does: `Sends ${fmt(lockInfo.surplus)} GRT of earned rewards to you. Not withdraw() — that one is forwarded to Staking and reverts '!tokens'.` },
  ];
}
